import { useState, useEffect } from 'react'
import DatasetBrowser from './DatasetBrowser'
import { useAppStore } from '../store'

const DEFAULT_WIDTH = 320
const COLLAPSED_STRIP = 4
const COLLAPSE_THRESHOLD = 60
const MIN_OPEN_WIDTH = 180

const Sidebar = () => {
  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const [dragging, setDragging] = useState(false)
  const [hovered, setHovered] = useState(false)
  const setSidebarWidth = useAppStore((state) => state.setSidebarWidth)
  const scope = useAppStore((state) => state.scope)

  const collapsed = width <= COLLAPSED_STRIP

  useEffect(() => {
    if (scope === 'collection') {
      setSidebarWidth(0)
      return
    }
    setSidebarWidth(width)
  }, [setSidebarWidth, width, scope])

  useEffect(() => () => setSidebarWidth(0), [setSidebarWidth])

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = collapsed ? 0 : width
    setDragging(true)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    const onMove = (ev: MouseEvent) => {
      setWidth(Math.max(0, startWidth + (ev.clientX - startX)))
    }

    const onUp = (ev: MouseEvent) => {
      const newWidth = Math.max(0, startWidth + (ev.clientX - startX))
      setWidth(newWidth < COLLAPSE_THRESHOLD ? COLLAPSED_STRIP : Math.max(MIN_OPEN_WIDTH, newWidth))
      setDragging(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  if (scope === 'collection') return null

  return (
    <>
      {/* Desktop sidebar */}
      <div style={{ display: 'none' }} className="ze-sidebar-desktop">
        <style>{`
          @media (min-width: 768px) {
            .ze-sidebar-desktop { display: block !important; }
            .ze-sidebar-mobile { display: none !important; }
          }
        `}</style>

        {/* Panel */}
        <div
          className="ze-sidebar"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width,
            background: 'var(--off)',
            borderRight: `${collapsed ? 2 : 1}px solid var(--rule)`,
            overflowY: collapsed ? 'hidden' : 'auto',
            zIndex: 1000,
            padding: collapsed ? 0 : '16px 16px 24px',
            boxSizing: 'border-box',
            transition: dragging ? 'none' : 'width 0.15s ease',
          }}
        >
          {!collapsed && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--teal)', flexShrink: 0 }} />
                <h1 style={{ fontFamily: 'var(--card-mono)', fontSize: '0.95rem', fontWeight: 600, letterSpacing: '0.04em', lineHeight: 1.2, margin: 0, color: 'var(--teal)', flex: 1 }}>
                  Zarr Explorer
                </h1>
              </div>
              <div className="ze-divider" />
              <DatasetBrowser />
            </>
          )}
        </div>

        {/* Drag handle — centered on the panel's right border */}
        <div
          onMouseDown={handleMouseDown}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            position: 'absolute',
            top: 0,
            left: width - 3,
            bottom: 0,
            width: 6,
            cursor: 'col-resize',
            zIndex: 1100,
            background: (hovered || dragging) ? 'rgba(0,0,0,0.06)' : 'transparent',
            transition: dragging ? 'none' : 'background 0.15s',
          }}
        />
      </div>

      {/* Mobile bottom panel */}
      <div
        className="ze-sidebar-mobile"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '50vh',
          background: 'var(--off)',
          overflowY: 'auto',
          zIndex: 1000,
          padding: '12px 16px',
          borderTop: '1px solid var(--rule)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--teal)', flexShrink: 0 }} />
          <h1 style={{ fontFamily: 'var(--card-mono)', fontSize: '0.95rem', fontWeight: 600, letterSpacing: '0.04em', lineHeight: 1.2, margin: 0, color: 'var(--teal)' }}>
            Zarr Explorer
          </h1>
        </div>
        <div className="ze-divider" />
        <DatasetBrowser />
      </div>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 767px) {
          .ze-sidebar-desktop { display: none !important; }
          .ze-sidebar-mobile { display: block !important; }
        }
        @media (min-width: 768px) {
          .ze-sidebar-desktop { display: block !important; }
          .ze-sidebar-mobile { display: none !important; }
        }
      `}</style>
    </>
  )
}

export default Sidebar
